// Service-period fields on the supplier invoice form
// (docs/features/periodisering/SPEC-service-period.md, D6).
//
// Facts prefill silently: a `found` period fills both dates with the hint
// "Hittad i fakturan: <evidence>"; an `ambiguous` one fills what it has and
// asks the user to check and accept it (an accept action, since it is not a
// fact); a `missing` one leaves the fields empty. Dates the user did not touch
// are not sent on create: the backend fills the confirmed columns from the
// extraction as `extracted`; touched or accepted dates are sent and become
// `user`. A single date is a form error, never a silently dropped period. Once
// a periodisation plan references the invoice the fields are read-only and
// link to the plan.
import { format } from 'date-fns';

function localDate(iso) {
  if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isoDate(date) {
  return date instanceof Date && !Number.isNaN(date.getTime()) ? format(date, 'yyyy-MM-dd') : null;
}

const AMBIGUOUS_HINT = 'Kontrollera perioden och bekräfta den, annars sparas ingen period';
export const SERVICE_PERIOD_HALF_ERROR = 'Tjänsteperioden måste ha både start och slut';

/** From an analyzeInvoice response or inbox ocr_fields: { start, end, hint, status }. */
export function servicePeriodPrefill(extracted) {
  const period = extracted && typeof extracted === 'object' ? extracted.servicePeriod : null;
  const empty = { start: null, end: null, hint: null, status: 'missing' };
  if (!period || typeof period !== 'object') return empty;
  if (period.status === 'found') {
    return {
      start: localDate(period.start),
      end: localDate(period.end),
      hint: period.evidence ? `Hittad i fakturan: ${period.evidence}` : 'Hittad i fakturan',
      status: 'found',
    };
  }
  if (period.status === 'ambiguous') {
    return { start: localDate(period.start), end: localDate(period.end), hint: AMBIGUOUS_HINT, status: 'ambiguous' };
  }
  return empty;
}

/** A change to one date; clearing either clears both (a period is both or none). */
export function servicePeriodChange(current, field, value) {
  if (value == null) return { start: null, end: null };
  return { ...current, [field]: value };
}

/**
 * The request fields. On create the extraction rides along unchanged; dates go
 * only when the user touched them and both are set (or both cleared on edit).
 */
export function servicePeriodPayload({ start, end, touched, extraction, isEditMode }) {
  const payload = {};
  if (!isEditMode && extraction && typeof extraction === 'object') payload.service_period_extraction = extraction;
  if (!touched) return payload;
  const startIso = isoDate(start);
  const endIso = isoDate(end);
  if (startIso && endIso) {
    payload.service_period_start = startIso;
    payload.service_period_end = endIso;
  } else if (isEditMode && !startIso && !endIso) {
    payload.service_period_start = null;
    payload.service_period_end = null;
  }
  return payload;
}

/** The form error for exactly one date set; null when both or none. */
export function servicePeriodError({ start, end }) {
  return !!isoDate(start) !== !!isoDate(end) ? SERVICE_PERIOD_HALF_ERROR : null;
}

/** An untouched ambiguous prefill with both dates needs the user's accept to be saved. */
export function servicePeriodNeedsAccept({ status, start, end, touched }) {
  return status === 'ambiguous' && !touched && !!isoDate(start) && !!isoDate(end);
}

/** Read-only state from the invoice DTO. */
export function servicePeriodLock(invoice) {
  const planId = invoice && invoice.periodisation_plan_id ? invoice.periodisation_plan_id : null;
  return { locked: !!planId, planId };
}

export function servicePeriodDatesFromInvoice(invoice) {
  return { start: localDate(invoice?.service_period_start), end: localDate(invoice?.service_period_end) };
}
